import type {ConnectElementConfig} from '../../../connect/ConnectJSInterface/ConnectElementList';
import {ComponentOverrideFlag} from '../../../utils/flags/flags';

export const defaultFinancingOfferParameter =
  'default-financing-offer' as const;
export const showFinancingSelectorParameter =
  'show-financing-selector' as const;
export const howCapitalWorksUrlParameter = 'how-capital-works-url' as const;
export const supportUrlParameter = 'support-url' as const;
export const capitalSchemeParameter = 'capital-scheme' as const;
export const directFinancingObjectParameter =
  'direct-financing-object' as const;
export const faqUrlParameter = 'faq-url' as const;

export const CapitalFinancingConfig: ConnectElementConfig = {
  Component: () => import('../../../components/Capital/CapitalFinancing'),
  enabledOverrideFlag: ComponentOverrideFlag.capital_financing,
  htmlAttributes: {
    [defaultFinancingOfferParameter]: {
      isInternal: false,
    },
    [showFinancingSelectorParameter]: {
      isInternal: false,
    },
    [howCapitalWorksUrlParameter]: {
      isInternal: false,
    },
    [faqUrlParameter]: {
      isInternal: false,
    },
    [supportUrlParameter]: {
      isInternal: false,
    },
    // Internal - to enable these for platforms, we'll need API review
    [capitalSchemeParameter]: {
      isInternal: true,
    },
    [directFinancingObjectParameter]: {
      isInternal: true,
    },
  },
  htmlEvents: ['onfinancingsloaded'],
  customHtmlElementMethods: {
    setDefaultFinancingOffer: {
      type: 'AttributeSettingMethod',
      attribute: defaultFinancingOfferParameter,
      isInternal: false,
      description: 'Sets the default financing offer to display',
    },
    setShowFinancingSelector: {
      type: 'AttributeSettingMethod',
      attribute: showFinancingSelectorParameter,
      isInternal: false,
      description:
        'Controls displaying the financing dropdown selector to allow the merchant to change the focused financing',
    },
    setHowCapitalWorksUrl: {
      type: 'AttributeSettingMethod',
      attribute: howCapitalWorksUrlParameter,
      isInternal: false,
      description: 'Sets the "How Capital Works" url to display',
    },
    setFaqUrl: {
      type: 'AttributeSettingMethod',
      attribute: faqUrlParameter,
      isInternal: false,
      description:
        'Sets the "How Capital Works" url to display. Backwards compatibility - this was renamed to setHowCapitalWorksUrl, but we need to keep this parameter working until all platforms stop using it.',
    },
    setSupportUrl: {
      type: 'AttributeSettingMethod',
      attribute: supportUrlParameter,
      isInternal: false,
      description: 'Sets the support url to display',
    },
    setOnFinancingsLoaded: {
      type: 'EventListenerMethod',
      event: 'onfinancingsloaded',
      isInternal: false,
      description:
        'Sets the event listener that allows a user to pass in a callback function that provides a total financings loaded count.',
    },
    // Internal - to enable these for platforms, we'll need API review
    setCapitalScheme: {
      type: 'AttributeSettingMethod',
      attribute: capitalSchemeParameter,
      isInternal: true,
      description:
        'Sets the CapitalScheme value, used to indicate when the component is rendering in certain Stripe-owned surfaces.',
    },
    setDirectFinancingObject: {
      type: 'AttributeSettingMethod',
      attribute: directFinancingObjectParameter,
      isInternal: true,
      description:
        'Sets the selected financing object to display for the detail pages in Direct Capital',
    },
    setOnChangeFinancing: {
      // we should consider exposing this one to platforms after we use it on direct
      type: 'EventListenerMethod',
      event: 'onchangefinancing',
      isInternal: true,
      description:
        'Sets the callback function for when a financing changes, either through default behavior or when selected in the dropdown.',
    },
  },
  releaseState: 'preview',
  sentryTeam: 'capital_core_products',
  teamOwner: 'capital-core-products',
  description:
    'Shows balance and reporting information for active accepted loans or past accepted loans',
  friendlyName: 'Capital financing',
  docSitePath: '/connect/supported-embedded-components/capital-financing',
  syntheticTestInfo: {
    testUrl:
      'https://amp.qa.corp.stripe.com/synthetics/us-west-2/qa-no-code-flxln-report-ee',
  },
};
